import { useCallback, useEffect, useState } from 'react'

// Central state for the "ask for notification permission" flow.
//
// Three things this owns:
//   - `permission` — mirrors Notification.permission so consumers can
//     branch on 'default' / 'granted' / 'denied' without re-querying.
//   - `shouldShowModal` — true exactly once per session when the
//     browser permission is 'default'. The dismiss/grant handlers
//     clear it; sessionStorage keeps it from re-firing on a back/
//     forward navigation that re-mounts <App />.
//   - `bannerVisible` — true whenever permission isn't 'granted' and
//     the user has been prompted at least once. Drives the persistent
//     "enable me" banner on the home page. Per-device dismissal hides
//     it for ~3 days (DISMISS_COOLDOWN_MS) before it comes back, so a
//     forgetful owner gets nudged again later.
//
// Why both a modal AND a banner: the modal is the first-time ask;
// the banner is the resurrection-after-ignore fallback. If a user
// chose 'denied' the modal stops appearing (browsers block silent
// re-prompts) but the banner keeps reminding them to go enable it
// from Settings or browser site settings.
const SESSION_FLAG = 'phoenix:notif-modal-shown-this-session'
const BANNER_DISMISSED_AT = 'phoenix:notif-banner-dismissed-at'
const MODAL_DISMISSED_AT = 'phoenix:notif-modal-dismissed-at'
const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000  // 3 days

function readPermission() {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission
}

function readBannerDismissed() {
  try {
    const raw = localStorage.getItem(BANNER_DISMISSED_AT)
    if (!raw) return false
    const ts = parseInt(raw, 10)
    if (Number.isNaN(ts)) return false
    return (Date.now() - ts) < DISMISS_COOLDOWN_MS
  } catch { return false }
}

export function useNotificationPrompt() {
  const [permission, setPermission] = useState(readPermission)
  const [shouldShowModal, setShouldShowModal] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(readBannerDismissed)

  // Decide on mount whether to surface the modal. Gates:
  //   - notifications must be supported by the browser
  //   - current permission is 'default' (we haven't asked yet)
  //   - the user hasn't dismissed the modal in the last few days
  //   - the modal hasn't already shown in *this session* (the session
  //     flag survives a route-level remount but resets on tab close)
  useEffect(() => {
    if (typeof Notification === 'undefined') return
    const perm = Notification.permission
    setPermission(perm)
    if (perm !== 'default') return
    try {
      if (sessionStorage.getItem(SESSION_FLAG)) return
      const modalDismissedAt = parseInt(localStorage.getItem(MODAL_DISMISSED_AT) || '0', 10)
      if (modalDismissedAt && (Date.now() - modalDismissedAt) < DISMISS_COOLDOWN_MS) return
      sessionStorage.setItem(SESSION_FLAG, '1')
      setShouldShowModal(true)
    } catch { /* private mode */ }
  }, [])

  // Modal "Not now" — close + remember so we don't pop up again
  // tomorrow. The banner takes over as the gentle reminder.
  const dismissModal = useCallback(() => {
    setShouldShowModal(false)
    try { localStorage.setItem(MODAL_DISMISSED_AT, String(Date.now())) } catch { /* noop */ }
  }, [])

  // Called after the browser prompt resolves so the banner / modal can
  // react to the new permission state without us re-reading on every
  // render.
  const updatePermission = useCallback((next) => {
    setPermission(next)
    if (next === 'granted') {
      setShouldShowModal(false)
      try {
        localStorage.removeItem(BANNER_DISMISSED_AT)
        localStorage.removeItem(MODAL_DISMISSED_AT)
      } catch { /* noop */ }
      setBannerDismissed(false)
    }
  }, [])

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true)
    try { localStorage.setItem(BANNER_DISMISSED_AT, String(Date.now())) } catch { /* noop */ }
  }, [])

  // Banner shows iff: notifications are supported, permission isn't
  // already granted, and the user hasn't dismissed it within the
  // cool-down. We don't gate on "have they been prompted" because the
  // banner is the safety net — if a user landed with permission already
  // 'denied' (e.g. they nuked it in browser settings), they should see
  // the banner pointing them to Settings to fix it.
  const bannerVisible = (
    permission !== 'unsupported' &&
    permission !== 'granted' &&
    !bannerDismissed
  )

  return {
    permission,
    shouldShowModal,
    bannerVisible,
    dismissModal,
    dismissBanner,
    updatePermission,
  }
}
